import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Search, Download, Edit3, Trash2, Key, X,
  ShieldCheck, Shield, User as UserIcon, Zap, Crown,
  ChevronLeft, ChevronRight, RefreshCw, CheckCircle2,
  Clock, AlertTriangle, ArrowUpRight, History } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PLAN_META = {
  free:       { label:'Free',       color:'var(--text3)',  bg:'var(--bg3)',       price:'₹0',      credits:50,    users:0   },
  individual: { label:'Individual', color:'var(--blue)',   bg:'var(--blue-bg)',   price:'₹100/mo', credits:100,   users:0   },
  pro:        { label:'Pro',        color:'var(--accent)', bg:'var(--accent-bg)', price:'₹500/mo', credits:500,   users:10  },
  pro_plus:   { label:'Pro Plus',   color:'var(--purple)', bg:'var(--purple-bg)', price:'₹1k/mo',  credits:500,   users:50  },
  enterprise: { label:'Enterprise', color:'var(--gold)',   bg:'var(--gold-bg)',   price:'Custom',  credits:99999, users:999 },
};
const STATUS_OPTS   = ['active','inactive','suspended'];
const ROLE_OPTS     = ['user','manager','admin'];
const PLAN_OPTS     = Object.keys(PLAN_META);
const INDUSTRIES    = ['Technology','Finance','Healthcare','Real Estate','Retail','Manufacturing','Education','Marketing','Legal','Construction','Hospitality','E-commerce','SaaS','Consulting','Other'];

const planBadge = p => {
  const m = PLAN_META[p] || PLAN_META.free;
  return <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:m.bg, color:m.color, textTransform:'uppercase', letterSpacing:.5 }}>{m.label}</span>;
};

// ── Assign Plan Modal ─────────────────────────────────────────────────────
function AssignPlanModal({ user, onClose, onSave }) {
  const [plan,    setPlan]    = useState(user.plan||'free');
  const [notes,   setNotes]   = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/admin/users/${user.id}/subscription-history`)
      .then(r => setHistory(r.data.history||[]))
      .catch(()=>{});
  }, [user.id]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/assign-plan`, { plan, notes });
      toast.success(r.data.message);
      onSave();
      onClose();
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div>
            <div className="modal-title">Assign Plan</div>
            <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>{user.first_name} {user.last_name} · {user.email}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Current */}
          <div style={{ display:'flex', gap:10, alignItems:'center', background:'var(--bg3)', borderRadius:9, padding:'10px 14px' }}>
            <span style={{ fontSize:13, color:'var(--text3)' }}>Current plan:</span>
            {planBadge(user.plan)}
            <span style={{ fontSize:13, color:'var(--text3)', marginLeft:'auto' }}>{user.search_credits} credits remaining</span>
          </div>

          {/* Plan selector */}
          <div className="form-group">
            <label className="form-label">Select New Plan</label>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {PLAN_OPTS.map(p => {
                const m = PLAN_META[p];
                return (
                  <label key={p} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', border:`2px solid ${plan===p?m.color:'var(--border)'}`, borderRadius:10, cursor:'pointer', background:plan===p?m.bg:'transparent', transition:'all .15s' }}>
                    <input type="radio" name="plan" value={p} checked={plan===p} onChange={()=>setPlan(p)} style={{ accentColor:m.color }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontWeight:700, fontSize:14 }}>{m.label}</span>
                        <span style={{ fontSize:12, color:m.color, fontWeight:600 }}>{m.price}</span>
                        {p===user.plan&&<span className="badge badge-green" style={{ fontSize:10 }}>Current</span>}
                      </div>
                      <div style={{ fontSize:12, color:'var(--text3)', marginTop:2 }}>
                        {m.credits===99999?'Unlimited':m.credits} credits/mo
                        {m.users>0?` · up to ${m.users} team members`:''}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">Internal Note (optional)</label>
            <textarea className="textarea" style={{ minHeight:60 }} placeholder="e.g. Upgraded for 3-month trial, payment received…" value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>

          {/* History */}
          {history.length>0&&(
            <div>
              <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'var(--text3)', marginBottom:8 }}>Subscription History</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {history.slice(0,5).map(h=>(
                  <div key={h.id} style={{ display:'flex', gap:10, fontSize:12.5, alignItems:'center', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ color:'var(--text3)', flexShrink:0 }}>{new Date(h.started_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
                    <span style={{ color:'var(--text3)' }}>{h.previous_plan} →</span>
                    {planBadge(h.plan)}
                    {h.assigned_by_name&&<span style={{ color:'var(--text3)', marginLeft:'auto' }}>by {h.assigned_by_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading||plan===user.plan}>
            {loading?<><div className="spinner spinner-sm"/>Saving…</>:<><Zap size={13}/>Assign Plan</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User Create/Edit Modal ────────────────────────────────────────────────
function UserModal({ user, onClose, onSave }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState(isEdit ? {
    firstName:user.first_name||'', lastName:user.last_name||'', email:user.email||'',
    company:user.company||'', jobTitle:user.job_title||'', industry:user.industry||'',
    phone:user.phone||'', city:user.city||'', country:user.country||'India',
    role:user.role||'user', status:user.status||'active',
    plan:user.plan||'free', searchCredits:user.search_credits||50, password:'',
  } : {
    firstName:'', lastName:'', email:'', password:'',
    company:'', jobTitle:'', industry:'', phone:'', city:'', country:'India',
    role:'user', status:'active', plan:'free', searchCredits:50,
  });
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  // Auto-set credits when plan changes (only if not manually edited)
  const onPlanChange = p => {
    set('plan', p);
    set('searchCredits', PLAN_META[p]?.credits||50);
  };

  const save = async () => {
    setLoading(true);
    try { await onSave(form, user?.id); onClose(); }
    catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">{isEdit?'Edit User':'Create New User'}</div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">First Name</label><input className="input" value={form.firstName} onChange={e=>set('firstName',e.target.value)} placeholder="John"/></div>
            <div className="form-group"><label className="form-label">Last Name</label><input className="input" value={form.lastName} onChange={e=>set('lastName',e.target.value)} placeholder="Doe"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Email</label><input className="input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="user@company.com"/></div>
            {!isEdit&&<div className="form-group"><label className="form-label">Password</label><input className="input" type="password" value={form.password} onChange={e=>set('password',e.target.value)} placeholder="Min 8 chars"/></div>}
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Company</label><input className="input" value={form.company} onChange={e=>set('company',e.target.value)} placeholder="Acme Corp"/></div>
            <div className="form-group"><label className="form-label">Job Title</label><input className="input" value={form.jobTitle} onChange={e=>set('jobTitle',e.target.value)} placeholder="Sales Manager"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Industry</label>
              <select className="select" value={form.industry} onChange={e=>set('industry',e.target.value)}>
                <option value="">Select…</option>{INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Phone</label><input className="input" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+91…"/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">City</label><input className="input" value={form.city} onChange={e=>set('city',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Country</label><input className="input" value={form.country} onChange={e=>set('country',e.target.value)}/></div>
          </div>
          <hr style={{ border:'none', borderTop:'1px solid var(--border)', margin:'4px 0' }}/>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Role</label>
              <select className="select" value={form.role} onChange={e=>set('role',e.target.value)}>
                {ROLE_OPTS.map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="select" value={form.status} onChange={e=>set('status',e.target.value)}>
                {STATUS_OPTS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Plan</label>
              <select className="select" value={form.plan} onChange={e=>onPlanChange(e.target.value)}>
                {PLAN_OPTS.map(p=><option key={p} value={p}>{PLAN_META[p].label} — {PLAN_META[p].price}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Search Credits</label>
              <input className="input" type="number" min="0" value={form.searchCredits} onChange={e=>set('searchCredits',Number(e.target.value))}/>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading?<><div className="spinner spinner-sm"/>Saving…</>:(isEdit?'Save Changes':'Create User')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main AdminUsers ────────────────────────────────────────────────────────
export default function AdminUsers() {
  const [users,    setUsers]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [roleF,    setRoleF]    = useState('');
  const [statusF,  setStatusF]  = useState('');
  const [planF,    setPlanF]    = useState('');
  const [page,     setPage]     = useState(1);
  const [editUser, setEdit]     = useState(null);
  const [planUser, setPlanUser] = useState(null);
  const [selected, setSelected] = useState([]);
  const PER = 20;

  const load = useCallback(async (pg=1) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users', { params:{ page:pg, limit:PER, search, role:roleF, status:statusF, plan:planF } });
      setUsers(r.data.users||[]);
      setTotal(r.data.total||0);
      setPage(pg);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [search, roleF, statusF, planF]);

  useEffect(() => { load(1); }, [search, roleF, statusF, planF]);

  const saveUser = async (form, id) => {
    if (id) {
      await api.put(`/admin/users/${id}`, { role:form.role, status:form.status, plan:form.plan, searchCredits:form.searchCredits, firstName:form.firstName, lastName:form.lastName, company:form.company, jobTitle:form.jobTitle, industry:form.industry, phone:form.phone, city:form.city, country:form.country });
      toast.success('User updated');
    } else {
      await api.post('/admin/users', { email:form.email, password:form.password, firstName:form.firstName, lastName:form.lastName, company:form.company, jobTitle:form.jobTitle, industry:form.industry, phone:form.phone, city:form.city, country:form.country, role:form.role, plan:form.plan, searchCredits:form.searchCredits });
      toast.success('User created');
    }
    load(page);
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success(`${name} deleted`); load(page); }
    catch(err) { toast.error(err.response?.data?.message||'Failed'); }
  };

  const resetPassword = async (id, name) => {
    const np = prompt(`New password for ${name} (min 8 chars):`);
    if (!np || np.length < 8) { toast.error('Min 8 characters'); return; }
    try { await api.post(`/admin/users/${id}/reset-password`, { newPassword:np }); toast.success('Password reset'); }
    catch { toast.error('Failed'); }
  };

  const addCredits = async (id, name) => {
    const c = prompt(`Add credits to ${name}:`);
    if (!c || isNaN(Number(c))) return;
    try { await api.post(`/admin/users/${id}/add-credits`, { credits:Number(c) }); toast.success(`Added ${c} credits`); load(page); }
    catch { toast.error('Failed'); }
  };

  const toggleSel = id => setSelected(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const allSel    = users.length>0 && users.every(u=>selected.includes(u.id));
  const totalPages= Math.ceil(total/PER);

  return (
    <div>
      <style>{`
        @media(max-width:768px){ .hide-mobile{display:none!important;} }
        .user-row:hover td { background:var(--bg2); }
      `}</style>

      {/* Header */}
      <div className="flex-between mb-20" style={{ flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, marginBottom:4 }}>User Management</h2>
          <p style={{ fontSize:13.5, color:'var(--text3)' }}><strong style={{ color:'var(--accent)' }}>{total}</strong> total users</p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={()=>load(page)}><RefreshCw size={13}/></button>
          <a href="/api/admin/export/users" className="btn btn-success btn-sm" style={{ textDecoration:'none' }}><Download size={13}/>Export CSV</a>
          <button className="btn btn-primary btn-sm" onClick={()=>setEdit({})}><UserPlus size={13}/>New User</button>
        </div>
      </div>

      {/* Plan summary pills */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:8, marginBottom:16 }}>
        {PLAN_OPTS.map(p => {
          const cnt = users.filter(u=>u.plan===p).length;
          const m   = PLAN_META[p];
          return (
            <div key={p} style={{ background:planF===p?m.bg:'var(--surface)', border:`1px solid ${planF===p?m.color:'var(--border)'}`, borderRadius:9, padding:'10px 12px', cursor:'pointer', textAlign:'center', transition:'all .15s' }}
              onClick={()=>setPlanF(planF===p?'':p)}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:m.color }}>{cnt}</div>
              <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:1 }}>{m.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="toolbar mb-16" style={{ flexWrap:'wrap', gap:8 }}>
        <div className="toolbar-left" style={{ flexWrap:'wrap', gap:8 }}>
          <div className="input-group" style={{ minWidth:180, flex:1, maxWidth:280 }}>
            <Search size={14} className="input-icon"/>
            <input className="input" placeholder="Search name, email, company…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="select" style={{ width:120 }} value={roleF} onChange={e=>setRoleF(e.target.value)}>
            <option value="">All Roles</option>
            {ROLE_OPTS.map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
          <select className="select" style={{ width:120 }} value={statusF} onChange={e=>setStatusF(e.target.value)}>
            <option value="">All Status</option>
            {STATUS_OPTS.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
          <select className="select" style={{ width:130 }} value={planF} onChange={e=>setPlanF(e.target.value)}>
            <option value="">All Plans</option>
            {PLAN_OPTS.map(p=><option key={p} value={p}>{PLAN_META[p].label}</option>)}
          </select>
          {(search||roleF||statusF||planF)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setRoleF('');setStatusF('');setPlanF('');}}>Clear</button>}
        </div>
        <span style={{ fontSize:13, color:'var(--text3)' }}>{total} users</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="empty-state"><div className="spinner spinner-lg"/></div>
      ) : users.length===0 ? (
        <div className="empty-state"><div style={{ fontSize:40 }}>👤</div><div style={{ fontWeight:600 }}>No users found</div></div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th><input type="checkbox" className="table-checkbox" checked={allSel} onChange={e=>e.target.checked?setSelected(users.map(u=>u.id)):setSelected([])}/></th>
                  <th>User</th>
                  <th>Plan</th>
                  <th className="hide-mobile">Credits</th>
                  <th className="hide-mobile">Status</th>
                  <th className="hide-mobile">Role</th>
                  <th className="hide-mobile">Searches</th>
                  <th className="hide-mobile">Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.id} className="user-row">
                    <td><input type="checkbox" className="table-checkbox" checked={selected.includes(u.id)} onChange={()=>toggleSel(u.id)}/></td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <div>
                          <div style={{ fontWeight:600, fontSize:13.5 }}>{u.first_name} {u.last_name}</div>
                          <div style={{ fontSize:12, color:'var(--text3)' }}>{u.email}</div>
                          {u.company&&<div style={{ fontSize:11.5, color:'var(--text3)' }}>{u.company}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        {planBadge(u.plan)}
                        {u.parent_email&&<div style={{ fontSize:11, color:'var(--text3)' }}>via {u.parent_email}</div>}
                      </div>
                    </td>
                    <td className="hide-mobile">
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <div style={{ width:40, height:5, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${Math.min(100,(u.search_credits/(PLAN_META[u.plan]?.credits||50))*100)}%`, background:u.search_credits<=5?'var(--red)':u.search_credits<=20?'var(--gold)':'var(--green)', borderRadius:3 }}/>
                        </div>
                        <span style={{ fontSize:13, fontWeight:600, color:u.search_credits<=5?'var(--red)':'var(--text2)' }}>{u.search_credits}</span>
                      </div>
                    </td>
                    <td className="hide-mobile">
                      <span className={`badge ${u.status==='active'?'badge-green':u.status==='suspended'?'badge-red':'badge-gray'}`}>
                        <span className="badge-dot"/>{u.status}
                      </span>
                    </td>
                    <td className="hide-mobile">
                      <span className={`badge ${u.role==='admin'?'badge-red':u.role==='manager'?'badge-purple':'badge-gray'}`}>
                        {u.role==='admin'?<ShieldCheck size={10}/>:u.role==='manager'?<Shield size={10}/>:<UserIcon size={10}/>} {u.role}
                      </span>
                    </td>
                    <td className="hide-mobile"><span style={{ fontWeight:600, color:'var(--accent)' }}>{u.total_searches||0}</span></td>
                    <td className="hide-mobile"><span style={{ fontSize:12.5, color:'var(--text3)', whiteSpace:'nowrap' }}>{u.created_at?new Date(u.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'2-digit'}):'—'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:4, flexWrap:'nowrap' }}>
                        {/* Assign Plan — most prominent action */}
                        <button className="btn btn-primary btn-sm" style={{ fontSize:11.5, padding:'4px 8px' }} onClick={()=>setPlanUser(u)} title="Assign Plan">
                          <Zap size={11}/>Plan
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setEdit(u)} title="Edit"><Edit3 size={13}/></button>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>addCredits(u.id,`${u.first_name} ${u.last_name}`)} title="Add Credits"><ArrowUpRight size={13}/></button>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>resetPassword(u.id,`${u.first_name} ${u.last_name}`)} title="Reset Password"><Key size={13}/></button>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>deleteUser(u.id,`${u.first_name} ${u.last_name}`)} style={{ color:'var(--red)' }} title="Delete"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages>1&&(
            <div className="pagination mt-16">
              <button className="page-btn" onClick={()=>load(page-1)} disabled={page===1}><ChevronLeft size={15}/></button>
              {Array.from({length:Math.min(totalPages,7)},(_,i)=>i+1).map(p=>(
                <button key={p} className={`page-btn${page===p?' active':''}`} onClick={()=>load(p)}>{p}</button>
              ))}
              {totalPages>7&&<span style={{ fontSize:13, color:'var(--text3)' }}>…{totalPages}</span>}
              <button className="page-btn" onClick={()=>load(page+1)} disabled={page===totalPages}><ChevronRight size={15}/></button>
            </div>
          )}
        </>
      )}

      {editUser!==null&&<UserModal user={editUser.id?editUser:null} onClose={()=>setEdit(null)} onSave={saveUser}/>}
      {planUser&&<AssignPlanModal user={planUser} onClose={()=>setPlanUser(null)} onSave={()=>load(page)}/>}
    </div>
  );
}